import { useState, useEffect, useRef } from 'react'
import { useNavigate } from 'react-router-dom'
import { ChevronLeft, Save, Building2, Upload, X, Camera } from 'lucide-react'
import axios from 'axios'
import { buildApiUrl } from '../../../config/api'
import { maskPhone, unmaskPhone, maskCEP, unmaskCEP } from '../../../utils/masks'
import { toast } from '../../../components/Toast'
import { Button, Card, Input, Select, Textarea, Switch, Spinner } from '../../../components/ui'
import ImageCropModal, { type CropResult } from '../../../components/ui/ImageCropModal'

interface Position { id: number; code: string; label: string; scope: string }
interface Party { id: number; number: number; abbreviation: string; name: string }
interface GeoState { id: number; code: string; name: string }
interface GeoCity { id: number; name: string }

export default function PoliticalConfigPage() {
  const navigate = useNavigate()
  const [loading, setLoading] = useState(true)
  const [savingOrg, setSavingOrg] = useState(false)
  const [savingOffice, setSavingOffice] = useState(false)
  const [positions, setPositions] = useState<Position[]>([])
  const [parties, setParties] = useState<Party[]>([])
  const [states, setStates] = useState<GeoState[]>([])
  const [cities, setCities] = useState<GeoCity[]>([])
  const [cepLoading, setCepLoading] = useState(false)
  const [showOfficeDetails, setShowOfficeDetails] = useState(false)

  const [org, setOrg] = useState({ name: '', name_corporate: '', logo_url: '' })
  const [uploadingLogo, setUploadingLogo] = useState(false)
  const [cropModalOpen, setCropModalOpen] = useState(false)

  const [form, setForm] = useState({
    political_position_id: '',
    party_id: '',
    scope: '',
    geo_state_id: '',
    geo_city_id: '',
    is_in_office: false,
    photo_url: '',
    biography: '',
    website: '',
    instagram: '',
    facebook: '',
    tiktok: '',
    youtube: '',
    twitter_x: '',
  })

  const [office, setOffice] = useState({
    name: '',
    phone: '',
    email: '',
    zip_code: '',
    street: '',
    number: '',
    neighborhood: '',
    complement: '',
    geo_city_id: '',
    city_display: '',
    office_start_date: '',
    office_end_date: '',
    notes: '',
  })

  const selectedPosition = positions.find(p => p.id === parseInt(form.political_position_id))
  const scope = selectedPosition?.scope || form.scope || ''

  useEffect(() => { fetchAll() }, [])

  async function fetchAll() {
    setLoading(true)
    try {
      const [configRes, posRes, partyRes, statesRes] = await Promise.all([
        axios.get(buildApiUrl('/v1/political/config')),
        axios.get(buildApiUrl('/v1/political/positions')),
        axios.get(buildApiUrl('/v1/political/parties')),
        axios.get(buildApiUrl('/v1/geo/states')),
      ])
      setPositions(posRes.data.data)
      setParties(partyRes.data.data)
      setStates(statesRes.data.data)

      const orgData = configRes.data.data.organization
      if (orgData) {
        setOrg({ name: orgData.name || '', name_corporate: orgData.name_corporate || '', logo_url: orgData.logo_url || '' })
      }

      const cfg = configRes.data.data.config
      const ofc = configRes.data.data.office

      if (cfg) {
        setForm({
          political_position_id: cfg.political_position_id ? String(cfg.political_position_id) : '',
          party_id: cfg.party_id ? String(cfg.party_id) : '',
          scope: cfg.scope || '',
          geo_state_id: cfg.geo_state_id ? String(cfg.geo_state_id) : '',
          geo_city_id: cfg.geo_city_id ? String(cfg.geo_city_id) : '',
          is_in_office: cfg.is_in_office || false,
          photo_url: cfg.photo_url || '',
          biography: cfg.biography || '',
          website: cfg.website || '',
          instagram: cfg.instagram || '',
          facebook: cfg.facebook || '',
          tiktok: cfg.tiktok || '',
          youtube: cfg.youtube || '',
          twitter_x: cfg.twitter_x || '',
        })
        if (cfg.geo_state_id) {
          const citiesRes = await axios.get(buildApiUrl(`/v1/geo/states/${cfg.geo_state_id}/cities`))
          setCities(citiesRes.data.data)
        }
      }

      if (ofc) {
        setOffice({
          name: ofc.name || '',
          phone: ofc.phone || '',
          email: ofc.email || '',
          zip_code: ofc.zip_code || '',
          street: ofc.street || '',
          number: ofc.number || '',
          neighborhood: ofc.neighborhood || '',
          complement: ofc.complement || '',
          geo_city_id: ofc.geo_city_id ? String(ofc.geo_city_id) : '',
          city_display: ofc.city_name && ofc.state_code ? `${ofc.city_name}/${ofc.state_code}` : '',
          office_start_date: ofc.office_start_date || '',
          office_end_date: ofc.office_end_date || '',
          notes: ofc.notes || '',
        })
        if (ofc.name || ofc.phone || ofc.email || ofc.zip_code) {
          setShowOfficeDetails(true)
        }
      }
    } catch { /* */ } finally { setLoading(false) }
  }

  useEffect(() => {
    if (form.geo_state_id && !loading) {
      axios.get(buildApiUrl(`/v1/geo/states/${form.geo_state_id}/cities`))
        .then(res => setCities(res.data.data))
        .catch(() => setCities([]))
      setForm(f => ({ ...f, geo_city_id: '' }))
    } else if (!form.geo_state_id) {
      setCities([])
    }
  }, [form.geo_state_id])

  async function lookupCep(cep: string) {
    if (cep.length < 8) return
    setCepLoading(true)
    try {
      const res = await axios.get(buildApiUrl(`/v1/geo/cep/${cep}`))
      const d = res.data.data
      setOffice(f => ({
        ...f,
        street: d.street || f.street,
        neighborhood: d.neighborhood || f.neighborhood,
        geo_city_id: d.geo_city_id?.toString() || f.geo_city_id,
        city_display: d.city_name && d.state_code ? `${d.city_name}/${d.state_code}` : f.city_display,
      }))
    } catch { /* */ } finally { setCepLoading(false) }
  }

  async function saveOrg() {
    if (!org.name.trim()) {
      toast('error', 'Nome político é obrigatório')
      return
    }
    if ((scope === 'STATE' || scope === 'MUNICIPAL') && !form.geo_state_id) {
      toast('error', 'Selecione o estado')
      return
    }
    if (scope === 'MUNICIPAL' && !form.geo_city_id) {
      toast('error', 'Selecione o município')
      return
    }
    setSavingOrg(true)
    try {
      await Promise.all([
        axios.put(buildApiUrl('/v1/political/organization'), org),
        axios.put(buildApiUrl('/v1/political/config'), {
          ...form,
          political_name: org.name,
          scope: scope || 'MUNICIPAL',
          political_position_id: form.political_position_id || null,
          party_id: form.party_id || null,
          geo_state_id: form.geo_state_id || null,
          geo_city_id: form.geo_city_id || null,
        }),
      ])
      toast('success', 'Dados salvos')
    } catch {
      toast('error', 'Erro ao salvar')
    } finally { setSavingOrg(false) }
  }

  async function saveOffice() {
    setSavingOffice(true)
    try {
      await axios.put(buildApiUrl('/v1/political/office'), {
        ...office,
        phone: unmaskPhone(office.phone),
        zip_code: unmaskCEP(office.zip_code),
        geo_city_id: office.geo_city_id || null,
      })
      toast('success', 'Gabinete salvo')
    } catch {
      toast('error', 'Erro ao salvar gabinete')
    } finally { setSavingOffice(false) }
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <Spinner size="lg" />
      </div>
    )
  }

  const positionOptions = positions.map(p => ({ value: String(p.id), label: p.label }))
  const partyOptions = parties.map(p => ({ value: String(p.id), label: `${p.abbreviation} - ${p.name}` }))
  const stateOptions = states.map(s => ({ value: String(s.id), label: s.name }))
  const cityOptions = cities.map(c => ({ value: String(c.id), label: c.name }))
  return (
    <div className="space-y-6">
      <div className="flex items-center gap-3">
        <button onClick={() => navigate('/settings/general')} className="h-9 w-9 flex items-center justify-center rounded-lg hover:bg-gray-200/60 text-gray-400 hover:text-gray-600 transition-colors">
          <ChevronLeft className="w-5 h-5" />
        </button>
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Configuração Política</h1>
          <p className="text-gray-500 mt-1">Dados do político e gabinete</p>
        </div>
      </div>

      <Card>
        <h3 className="text-lg font-semibold text-gray-800 mb-6">Dados da Organização</h3>

        {/* Logo */}
        <div className="flex items-start gap-6 mb-6">
          <div className="relative group">
            <div className="w-24 h-24 rounded-2xl border-2 border-dashed border-gray-300 bg-gray-50 overflow-hidden flex items-center justify-center">
              {org.logo_url ? (
                <img src={org.logo_url} alt="Logo" className="w-full h-full object-cover" />
              ) : (
                <Building2 className="w-8 h-8 text-gray-400" />
              )}
            </div>
            {org.logo_url && (
              <button
                type="button"
                onClick={async () => {
                  try {
                    await axios.delete(buildApiUrl('/v1/organization/logo'))
                    setOrg(f => ({ ...f, logo_url: '' }))
                    toast('success', 'Logo removido')
                  } catch { toast('error', 'Erro ao remover') }
                }}
                className="absolute -top-2 -right-2 w-6 h-6 rounded-full bg-red-500 text-white flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
              >
                <X className="w-3.5 h-3.5" />
              </button>
            )}
          </div>
          <div className="flex-1">
            <p className="text-sm font-medium text-gray-700 mb-1">Logomarca</p>
            <p className="text-xs text-gray-500 mb-3">PNG ou JPG, máximo 5 MB. Usada na comunidade e no app.</p>
            <Button
              type="button"
              variant="secondary"
              size="sm"
              loading={uploadingLogo}
              onClick={() => setCropModalOpen(true)}
            >
              <Upload className="w-4 h-4 mr-1.5" />
              {org.logo_url ? 'Trocar logo' : 'Enviar logo'}
            </Button>
          </div>

          <ImageCropModal
            open={cropModalOpen}
            onClose={() => setCropModalOpen(false)}
            aspectRatio={1}
            loading={uploadingLogo}
            title="Recortar Logomarca"
            onCropComplete={async (result: CropResult) => {
              setUploadingLogo(true)
              try {
                const fd = new FormData()
                fd.append('file', result.file)
                const res = await axios.post(buildApiUrl('/v1/organization/logo'), fd, {
                  headers: { 'Content-Type': 'multipart/form-data' },
                })
                setOrg(f => ({ ...f, logo_url: res.data.url }))
                toast('success', 'Logo atualizado')
                setCropModalOpen(false)
              } catch { toast('error', 'Erro ao enviar logo') }
              finally { setUploadingLogo(false) }
            }}
          />
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <Input
            label="Nome político"
            value={org.name}
            onChange={e => setOrg(f => ({ ...f, name: e.target.value }))}
            placeholder="Nome usado politicamente"
            required
          />
          <Input
            label="Nome completo / Razão social"
            value={org.name_corporate}
            onChange={e => setOrg(f => ({ ...f, name_corporate: e.target.value }))}
            placeholder="Nome completo ou razão social"
          />
        </div>

        <div className="mt-4">
          <Textarea
            label="Biografia"
            value={form.biography}
            onChange={e => setForm(f => ({ ...f, biography: e.target.value }))}
            placeholder="Breve biografia política"
            rows={3}
          />
        </div>

        {/* Abrangência */}
        <div className="border-t border-gray-100 mt-6 pt-4">
          <p className="text-sm font-medium text-gray-700 mb-3">Abrangência</p>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <Select
              label="Escopo"
              value={scope}
              onChange={e => setForm(f => ({ ...f, scope: e.target.value, geo_state_id: '', geo_city_id: '' }))}
              options={[
                { value: 'NATIONAL', label: 'Nacional' },
                { value: 'STATE', label: 'Estadual' },
                { value: 'MUNICIPAL', label: 'Municipal' },
              ]}
              placeholder="Selecione"
            />
            {(scope === 'STATE' || scope === 'MUNICIPAL') && (
              <Select
                label="Estado"
                value={form.geo_state_id}
                onChange={e => setForm(f => ({ ...f, geo_state_id: e.target.value }))}
                options={stateOptions}
                placeholder="Selecione"
              />
            )}
            {scope === 'MUNICIPAL' && (
              <Select
                label="Cidade"
                value={form.geo_city_id}
                onChange={e => setForm(f => ({ ...f, geo_city_id: e.target.value }))}
                options={cityOptions}
                placeholder="Selecione"
              />
            )}
          </div>
        </div>

        <h4 className="text-sm font-semibold text-gray-600 mt-6 mb-4">Redes Sociais</h4>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          <Input label="Website" value={form.website} onChange={e => setForm(f => ({ ...f, website: e.target.value }))} placeholder="https://..." />
          <Input label="Instagram" value={form.instagram} onChange={e => setForm(f => ({ ...f, instagram: e.target.value }))} placeholder="@usuario" />
          <Input label="Facebook" value={form.facebook} onChange={e => setForm(f => ({ ...f, facebook: e.target.value }))} placeholder="@pagina" />
          <Input label="TikTok" value={form.tiktok} onChange={e => setForm(f => ({ ...f, tiktok: e.target.value }))} placeholder="@usuario" />
          <Input label="YouTube" value={form.youtube} onChange={e => setForm(f => ({ ...f, youtube: e.target.value }))} placeholder="Canal ou @usuario" />
          <Input label="X (Twitter)" value={form.twitter_x} onChange={e => setForm(f => ({ ...f, twitter_x: e.target.value }))} placeholder="@usuario" />
        </div>

        <div className="mt-6 flex justify-end">
          <Button onClick={saveOrg} loading={savingOrg}>
            <Save className="w-4 h-4 mr-2" />Salvar
          </Button>
        </div>
      </Card>

      <Card>
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-2">
            <Building2 className="w-5 h-5 text-gray-400" />
            <h3 className="text-lg font-semibold text-gray-800">Mandato</h3>
          </div>
          <div className="flex items-center gap-3">
            <span className="text-sm text-gray-700">Em mandato atualmente</span>
            <Switch
              checked={form.is_in_office}
              onChange={v => setForm(f => ({ ...f, is_in_office: v }))}
            />
          </div>
        </div>

        {form.is_in_office && (<>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <Select
              label="Cargo"
              value={form.political_position_id}
              onChange={e => setForm(f => ({ ...f, political_position_id: e.target.value }))}
              options={positionOptions}
              placeholder="Selecione"
            />
            <Select
              label="Partido"
              value={form.party_id}
              onChange={e => setForm(f => ({ ...f, party_id: e.target.value }))}
              options={partyOptions}
              placeholder="Selecione"
            />
            <Input label="Início do mandato" type="date" value={office.office_start_date} onChange={e => setOffice(f => ({ ...f, office_start_date: e.target.value }))} />
            <Input label="Fim do mandato" type="date" value={office.office_end_date} onChange={e => setOffice(f => ({ ...f, office_end_date: e.target.value }))} />
          </div>

          {/* Gabinete */}
          <div className="border-t border-gray-100 mt-6 pt-4">
            <div className="flex items-center gap-3 mb-4">
              <Switch
                checked={showOfficeDetails}
                onChange={setShowOfficeDetails}
              />
              <span className="text-sm text-gray-700">Incluir informações do gabinete</span>
            </div>

            {showOfficeDetails && (
              <>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <Input label="Nome do gabinete" value={office.name} onChange={e => setOffice(f => ({ ...f, name: e.target.value }))} placeholder="Ex: Gabinete do Deputado..." />
                  <Input label="Telefone" value={maskPhone(office.phone)} onChange={e => setOffice(f => ({ ...f, phone: unmaskPhone(e.target.value) }))} placeholder="(00) 00000-0000" />
                  <Input label="E-mail" value={office.email} onChange={e => setOffice(f => ({ ...f, email: e.target.value }))} placeholder="gabinete@email.com" type="email" />
                </div>

                {/* Endereço do gabinete */}
                <div className="border-t border-gray-100 mt-4 pt-4">
                  <p className="text-sm font-medium text-gray-700 mb-3">Endereço do gabinete</p>
                  <div className="grid grid-cols-3 gap-4">
                    <div>
                      <label className="block text-xs text-gray-500 mb-1">CEP</label>
                      <div className="relative">
                        <input
                          value={maskCEP(office.zip_code)}
                          onChange={e => {
                            const raw = unmaskCEP(e.target.value)
                            setOffice(f => ({ ...f, zip_code: raw }))
                            if (raw.length === 8) lookupCep(raw)
                          }}
                          placeholder="00000-000"
                          className="h-11 w-full rounded-lg border border-gray-300 px-4 pr-10 text-sm shadow-xs placeholder:text-gray-400 focus:border-brand-300 focus:ring-3 focus:ring-brand-500/10 outline-none"
                        />
                        {cepLoading && (
                          <div className="absolute right-3 top-1/2 -translate-y-1/2">
                            <div className="w-4 h-4 border-2 border-brand-200 border-t-brand-500 rounded-full animate-spin" />
                          </div>
                        )}
                      </div>
                    </div>
                    <div className="col-span-2">
                      <label className="block text-xs text-gray-500 mb-1">Rua</label>
                      <input value={office.street} onChange={e => setOffice(f => ({ ...f, street: e.target.value }))}
                        className="h-11 w-full rounded-lg border border-gray-300 px-4 text-sm shadow-xs focus:border-brand-300 focus:ring-3 focus:ring-brand-500/10 outline-none" />
                    </div>
                  </div>
                  <div className="grid grid-cols-4 gap-4 mt-3">
                    <div>
                      <label className="block text-xs text-gray-500 mb-1">Número</label>
                      <input value={office.number} onChange={e => setOffice(f => ({ ...f, number: e.target.value }))}
                        className="h-11 w-full rounded-lg border border-gray-300 px-4 text-sm shadow-xs focus:border-brand-300 focus:ring-3 focus:ring-brand-500/10 outline-none" />
                    </div>
                    <div>
                      <label className="block text-xs text-gray-500 mb-1">Bairro</label>
                      <input value={office.neighborhood} onChange={e => setOffice(f => ({ ...f, neighborhood: e.target.value }))}
                        className="h-11 w-full rounded-lg border border-gray-300 px-4 text-sm shadow-xs focus:border-brand-300 focus:ring-3 focus:ring-brand-500/10 outline-none" />
                    </div>
                    <div>
                      <label className="block text-xs text-gray-500 mb-1">Complemento</label>
                      <input value={office.complement} onChange={e => setOffice(f => ({ ...f, complement: e.target.value }))}
                        className="h-11 w-full rounded-lg border border-gray-300 px-4 text-sm shadow-xs focus:border-brand-300 focus:ring-3 focus:ring-brand-500/10 outline-none" />
                    </div>
                    <div>
                      <label className="block text-xs text-gray-500 mb-1">Cidade/UF</label>
                      <input value={office.city_display} readOnly placeholder="Preenchido pelo CEP"
                        className="h-11 w-full rounded-lg border border-gray-200 bg-gray-50 px-4 text-sm text-gray-600 cursor-not-allowed" />
                    </div>
                  </div>
                </div>

                <div className="mt-4">
                  <Textarea label="Observações" value={office.notes} onChange={e => setOffice(f => ({ ...f, notes: e.target.value }))} rows={2} />
                </div>
              </>
            )}
          </div>

          <div className="mt-6 flex justify-end">
            <Button onClick={saveOffice} loading={savingOffice}>
              <Save className="w-4 h-4 mr-2" />Salvar mandato
            </Button>
          </div>
        </>)}
      </Card>
    </div>
  )
}
